/* Bower — This Week + Closer Look
 *
 * Owns:
 *   - Day-pill state on the tile. (Modal no longer carries pills —
 *     it's a viewer-only lightbox now, so the previous tile↔modal
 *     sync is moot.)
 *   - Closer Look lightbox open/close. Click a tile's expand button
 *     to open the matching modal; close via X, scrim, or Esc. URL
 *     gains ?look=<handle> while open; strips on close. Body scroll
 *     lock + focus trap mirror the cart-drawer pattern.
 *   - Lightbox image carousel: prev / next arrow buttons plus
 *     keyboard ← / → cycle through `product.images`. The "01 / 06"
 *     counter in the corner reflects state.
 *   - Send button (tile only): AJAX POST /cart/add.js with the
 *     variant + a `_delivery_date` line-item property formatted as
 *     "Wednesday 7 May". On success, dispatch `bower:cart:added` so
 *     the cart drawer's existing listener auto-opens.
 *
 * Public API: window.bowerThisWeek.{ open(handle), close() } for
 * the rare external trigger (e.g. linking from email to a specific
 * arrangement).
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(init);

  function init() {
    const root = document.querySelector('.bower-week');
    if (!root) return;

    const looks = document.querySelectorAll('[data-bower-look]');
    let lastFocus = null;
    let activeLook = null;

    // ---- Day-pill state (tile only) ---------------------------------
    document.addEventListener('click', (e) => {
      const pill = e.target.closest('[data-bower-pill]');
      if (!pill) return;
      e.preventDefault();
      selectPill(pill);
    });

    function selectPill(pill) {
      const day = pill.dataset.day;
      const tile = pill.closest('[data-product-handle]');
      if (!tile || !day) return;
      // Pills only live on the tile now (the lightbox dropped the
      // PDP UI). Just flip the selected one within this tile.
      tile.querySelectorAll('[data-bower-pill]').forEach((p) => {
        const on = p.dataset.day === day;
        p.classList.toggle('is-selected', on);
        p.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    }

    // ---- Modal: open via tile expand button --------------------------
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-bower-tile-expand]');
      if (!btn) return;
      e.preventDefault();
      const tile = btn.closest('[data-product-handle]');
      if (!tile) return;
      openLook(tile.dataset.productHandle, btn);
    });

    // ---- Modal: close (X / scrim / Back link) -----------------------
    document.addEventListener('click', (e) => {
      if (!activeLook) return;

      if (e.target.closest('[data-bower-look-close]')) {
        e.preventDefault();
        closeLook();
        return;
      }
      const scrim = activeLook.querySelector('[data-bower-look-scrim]');
      if (e.target === scrim) {
        closeLook();
      }
    });

    // ---- Lightbox + tile: prev / next arrows + counter --------------
    //
    // The same crossfade-cycle drives both surfaces — only the data
    // attributes differ (bower-look-* on the lightbox,
    // bower-tile-* on the tile inline carousel). Click handlers
    // delegate on document; e.stopPropagation isn't needed because
    // the arrow buttons are siblings of the expand button (not
    // nested inside it), so clicks land on the arrow without bubbling
    // through the expand-modal target.
    document.addEventListener('click', (e) => {
      const lookArrow = e.target.closest('[data-bower-look-prev], [data-bower-look-next]');
      if (lookArrow) {
        e.preventDefault();
        const look = lookArrow.closest('[data-bower-look]');
        if (!look) return;
        cycleImages(
          look,
          lookArrow.matches('[data-bower-look-prev]') ? -1 : 1,
          '[data-bower-look-hero]',
          '[data-bower-look-current]'
        );
        return;
      }

      const tileArrow = e.target.closest('[data-bower-tile-prev], [data-bower-tile-next]');
      if (tileArrow) {
        e.preventDefault();
        const tile = tileArrow.closest('[data-bower-tile]');
        if (!tile) return;
        cycleImages(
          tile,
          tileArrow.matches('[data-bower-tile-prev]') ? -1 : 1,
          '[data-bower-tile-img]',
          '[data-bower-tile-current]'
        );
      }
    });

    // Generic crossfade-cycle. `imgSel` matches the elements with
    // `is-current` toggling; `currentSel` matches the counter text
    // node (zero-padded "01 / 06" format).
    //
    // Wraps at both ends — feels closer to a photo viewer than a
    // step-through. Returns early on a single-image scope so the
    // counter doesn't churn.
    function cycleImages(scope, dir, imgSel, currentSel) {
      const imgs = Array.from(scope.querySelectorAll(imgSel));
      if (imgs.length <= 1) return;
      const currentIdx = imgs.findIndex((img) => img.classList.contains('is-current'));
      const nextIdx = (currentIdx + dir + imgs.length) % imgs.length;
      imgs.forEach((img, i) => img.classList.toggle('is-current', i === nextIdx));
      const counterEl = scope.querySelector(currentSel);
      if (counterEl) counterEl.textContent = String(nextIdx + 1).padStart(2, '0');
    }

    // ---- Modal: open hook on URL ?look=<handle> ----------------------
    const params = new URLSearchParams(window.location.search);
    const initialHandle = params.get('look');
    if (initialHandle) openLook(initialHandle, null);

    function openLook(handle, returnFocusEl) {
      const look = document.querySelector(
        '[data-bower-look][data-product-handle="' + cssEscape(handle) + '"]'
      );
      if (!look) return;
      if (look === activeLook) return;

      // If another modal is already open (e.g. via the public API
      // jumping between products), close it first so we don't end up
      // with two modals with body-scroll-lock fighting each other.
      if (activeLook && activeLook !== look) {
        closeLook();
      }

      lastFocus = returnFocusEl || document.activeElement;
      activeLook = look;
      look.removeAttribute('hidden');
      look.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => look.classList.add('is-open'));
      document.body.style.overflow = 'hidden';

      // Reset to the first image on each open. DOM state from a
      // previous open survives in place (we only swap classes, not
      // markup), so re-opening at image 3 would otherwise show a
      // mismatched counter and skip the user past the hero shot.
      const heros = look.querySelectorAll('[data-bower-look-hero]');
      heros.forEach((img, i) => img.classList.toggle('is-current', i === 0));
      const counterEl = look.querySelector('[data-bower-look-current]');
      if (counterEl) counterEl.textContent = '01';

      // Bookmark the modal in the URL.
      const url = new URL(window.location.href);
      url.searchParams.set('look', handle);
      window.history.replaceState({}, '', url.toString());

      // Focus the close button — the trap will keep tab inside.
      const closeBtn = look.querySelector('[data-bower-look-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
      document.addEventListener('keydown', onKeydown);
    }

    function closeLook() {
      if (!activeLook) return;
      const look = activeLook;
      activeLook = null;
      look.classList.remove('is-open');
      look.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);

      // After transition, hide so it's not in tab order.
      const panel = look.querySelector('.bower-look__panel');
      const ms = panel
        ? parseDuration(getComputedStyle(panel).transitionDuration)
        : 220;
      setTimeout(() => {
        if (!look.classList.contains('is-open')) {
          look.setAttribute('hidden', '');
        }
      }, ms + 40);

      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }

      // Strip ?look from the URL so a refresh doesn't re-open.
      if (window.location.search.includes('look=')) {
        const url = new URL(window.location.href);
        url.searchParams.delete('look');
        window.history.replaceState({}, '', url.toString());
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeLook();
        return;
      }
      if (e.key === 'ArrowLeft' && activeLook) {
        e.preventDefault();
        cycleImages(activeLook, -1, '[data-bower-look-hero]', '[data-bower-look-current]');
        return;
      }
      if (e.key === 'ArrowRight' && activeLook) {
        e.preventDefault();
        cycleImages(activeLook, 1, '[data-bower-look-hero]', '[data-bower-look-current]');
        return;
      }
      if (e.key === 'Tab' && activeLook) trapFocus(e, activeLook);
    }

    function trapFocus(e, el) {
      const focusables = el.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function parseDuration(str) {
      if (!str) return 220;
      const parts = String(str).split(',').map((s) => s.trim());
      let max = 0;
      for (const p of parts) {
        const n = parseFloat(p);
        if (Number.isNaN(n)) continue;
        const ms = p.endsWith('ms') ? n : n * 1000;
        if (ms > max) max = ms;
      }
      return max || 220;
    }

    // ---- Send: AJAX add to cart with delivery-day property -----------
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-bower-send]');
      if (!btn) return;
      e.preventDefault();

      const owner = btn.closest('[data-product-handle][data-variant-id]');
      if (!owner) return;

      const variantId = owner.dataset.variantId;
      const handle = owner.dataset.productHandle;

      // Read the selected pill from THIS UI (tile or modal). If the
      // user has set a day in the other UI but not here, the synced
      // class/attribute means it's already up to date.
      const selected = owner.querySelector('[data-bower-pill].is-selected');
      const day = selected ? selected.dataset.day : null;
      if (!day) {
        console.warn('[bower-this-week] no delivery day selected for', handle);
        return;
      }

      const deliveryDate = formatDeliveryDate(day);

      btn.disabled = true;
      // Find the leading text node ("Send"). Defensive against
      // future markup formatting that might leave a whitespace text
      // node first — we want the first NON-empty text node.
      const textNode = Array.prototype.find.call(
        btn.childNodes,
        (n) => n.nodeType === 3 && n.nodeValue && n.nodeValue.trim().length > 0
      );
      const originalText = textNode ? textNode.nodeValue : 'Send';
      if (textNode) textNode.nodeValue = 'Sending… ';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: { _delivery_date: deliveryDate },
          }),
        });
        if (!res.ok) throw new Error('cart add failed');
        const cartLine = await res.json();

        // Refresh authoritative cart so the count is right.
        const cartRes = await fetch('/cart.js');
        const cart = cartRes.ok ? await cartRes.json() : null;

        // Tell the cart drawer to refresh + open. The drawer's
        // existing listener handles both.
        document.dispatchEvent(new CustomEvent('bower:cart:added', {
          detail: { cartLine, cart, item_count: cart ? cart.item_count : null },
        }));

      } catch (err) {
        console.error('[bower-this-week] send failed', err);
      } finally {
        btn.disabled = false;
        if (textNode) textNode.nodeValue = originalText;
      }
    });

    // ---- Helpers -----------------------------------------------------

    // Format the selected day into "Wednesday 7 May" by finding the
    // next occurrence of that weekday from today (or today itself if
    // it matches). Used as the line-item property the cart drawer
    // already reads.
    function formatDeliveryDate(dayShort) {
      const days = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      const longDays = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const target = days[dayShort];
      if (target === undefined) return dayShort;

      const now = new Date();
      const today = now.getDay();
      let delta = target - today;
      // If today's day matches OR the target has passed this week,
      // jump to next week's instance — the next-drop cadence is
      // weekly and orders take a full week to deliver.
      if (delta <= 0) delta += 7;
      const date = new Date(now);
      date.setDate(now.getDate() + delta);
      return longDays[date.getDay()] + ' ' + date.getDate() + ' ' + months[date.getMonth()];
    }

    // CSS-escape a handle for selector use. Shopify product handles
    // are kebab-case, but be defensive against numbers/punctuation.
    function cssEscape(str) {
      if (window.CSS && CSS.escape) return CSS.escape(str);
      return String(str).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
    }

    // ---- Public API --------------------------------------------------
    window.bowerThisWeek = {
      open: (handle) => openLook(handle, null),
      close: closeLook,
    };
  }
})();
